import { RotateCcw } from "lucide-react";
import { PATHWAYS, PROJECTS, CROSS_LINKS, slug, pathwayOf } from "@/components/knowledge/graphData";

const Chip = ({ children, color, onClick, active, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-pressed={active}
    className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-colors duration-200 ${active ? "border-ink bg-white" : "border-ink/10 bg-white/70 hover:border-ink/40"}`}>
    {color && <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} aria-hidden="true" />}
    {children}
  </button>
);

export const KnowledgeControls = ({ selected, onReset }) => (
  <div className="flex min-h-[40px] flex-wrap items-center justify-end" data-testid="kg-controls">
    {selected && (
      <button type="button" onClick={onReset} data-testid="kg-reset"
        className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-bold text-cream transition-colors hover:bg-ink-soft">
        <RotateCcw className="h-4 w-4" aria-hidden="true" /> Show full network
      </button>
    )}
  </div>
);

const conceptTitle = (id) => PATHWAYS.flatMap((p) => p.concepts).find((c) => slug(c) === id);
// Concepts are clickable only where they exist as nodes (the simplified mobile map lists them here instead)
const ConceptChips = ({ names, onSelect, has }) => (
  <div className="mt-2 flex flex-wrap gap-2">
    {names.map((c) => {
      const p = PATHWAYS.find((pw) => pw.concepts.includes(c));
      return has(slug(c)) ? (
        <Chip key={c} color={p.stroke} onClick={() => onSelect(slug(c))} testid={`kg-chip-${slug(c)}`}>{c}</Chip>
      ) : (
        <span key={c} data-testid={`kg-chip-${slug(c)}`} className="inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white px-3.5 py-1.5 text-sm font-bold">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.stroke }} aria-hidden="true" />{c}
        </span>
      );
    })}
  </div>
);
const ProjectChips = ({ projects, onSelect }) => (
  <div className="mt-2 flex flex-wrap gap-2">
    {projects.map((p) => <Chip key={p.id} onClick={() => onSelect(p.id)} testid={`kg-chip-${p.id}`}>{p.title}</Chip>)}
  </div>
);
const Sub = ({ children }) => <p className="mt-4 text-xs font-bold uppercase tracking-[0.14em] text-ink-muted">{children}</p>;

const HubInfo = ({ p, onSelect, has }) => (
  <>
    <h3 className="text-2xl font-semibold">{p.title}</h3>
    <Sub>Knowledge concepts</Sub>
    <ConceptChips names={p.concepts} onSelect={onSelect} has={has} />
    <Sub>Applied in real-world themes</Sub>
    <ProjectChips projects={PROJECTS.filter((pr) => pr.links.some((c) => p.concepts.includes(c)))} onSelect={onSelect} />
  </>
);

const ProjectInfo = ({ pr, onSelect, has }) => {
  const groups = PATHWAYS.filter((p) => p.concepts.some((c) => pr.links.includes(c)));
  return (
  <>
    <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink-muted">Interdisciplinary theme</p>
    <h3 className="mt-1 text-2xl font-semibold">{pr.title}</h3>
    <p className="mt-1 text-ink-soft">Draws on {pr.links.length} knowledge concepts across {groups.length} learning pathways:</p>
    <div className="mt-2 grid gap-x-8 sm:grid-cols-2">
      {groups.map((p) => (
        <div key={p.id}>
          <Sub>{p.title}</Sub>
          <ConceptChips names={p.concepts.filter((c) => pr.links.includes(c))} onSelect={onSelect} has={has} />
        </div>
      ))}
    </div>
  </>
  );
};

const ConceptInfo = ({ id, node, onSelect, has }) => {
  const title = conceptTitle(id);
  const related = CROSS_LINKS.filter((l) => l.includes(title)).map((l) => (l[0] === title ? l[1] : l[0]));
  return (
    <>
      <h3 className="text-2xl font-semibold">{title}</h3>
      <p className="mt-1 text-ink-soft">Part of {pathwayOf(node.pathway).title}</p>
      {related.length > 0 && (<><Sub>Connects with</Sub><ConceptChips names={related} onSelect={onSelect} has={has} /></>)}
      <Sub>Applied in real-world themes</Sub>
      <ProjectChips projects={PROJECTS.filter((pr) => pr.links.includes(title))} onSelect={onSelect} />
    </>
  );
};

export const KnowledgePanel = ({ selected, graph, onSelect }) => {
  const node = selected && graph.byId[selected];
  const has = (id) => !!graph.byId[id];
  if (!node) return null;
  return (
    <div className="card-soft p-6 sm:p-8" aria-live="polite" data-testid="kg-panel">
      {node?.kind === "hub" && <HubInfo p={pathwayOf(node.pathway)} onSelect={onSelect} has={has} />}
      {node?.kind === "project" && <ProjectInfo pr={PROJECTS.find((p) => p.id === node.id)} onSelect={onSelect} has={has} />}
      {node?.kind === "concept" && <ConceptInfo id={node.id} node={node} onSelect={onSelect} has={has} />}
    </div>
  );
};
